import { useState } from "react";
import { ChevronDown, SlidersHorizontal } from "lucide-react";
import { Input } from "@/shared/components/Input";
import { CampoSelect } from "@/modules/visitantes/components/Campos";
import {
  erroIdade,
  erroPeriodo,
  type FiltroFaixa,
  type FiltroPeriodo,
  type FiltroSituacao,
  type FiltrosVisitantes as Filtros,
  type Ordenacao,
} from "@/modules/visitantes/utils/filtros";
import { FAIXAS, hojeSaoPaulo } from "@/modules/visitantes/utils/regras";

interface Props {
  filtros: Filtros;
  /** Quantos dos 3 filtros (situação, data, faixa) estão ligados — vira o número no botão. */
  ativos: number;
  /** Mostra a ordenação "Mais visitas" etc. sempre; a situação só interessa a quem acompanha. */
  onMudar: (parcial: Partial<Filtros>) => void;
  onLimpar: () => void;
}

/** Caixa "Filtrar": situação, data da visita, faixa etária (com faixa personalizada) e ordem. */
export function FiltrosVisitantes({ filtros, ativos, onMudar, onLimpar }: Props) {
  const [aberto, setAberto] = useState(ativos > 0);
  const hoje = hojeSaoPaulo();
  const msgPeriodo = erroPeriodo(filtros);
  const msgIdade = erroIdade(filtros);

  return (
    <section aria-label="Filtros" className="mb-3 rounded-xl border border-border bg-surface">
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        aria-expanded={aberto}
        className="flex min-h-11 w-full items-center justify-between gap-2 px-4 text-sm font-semibold text-text"
      >
        <span className="inline-flex items-center gap-2">
          <SlidersHorizontal size={16} aria-hidden="true" /> Filtrar
          {ativos > 0 && (
            <span className="rounded-full bg-primary px-2 py-0.5 text-[11px] font-bold text-on-primary">
              {ativos}
            </span>
          )}
        </span>
        <ChevronDown
          size={16}
          aria-hidden="true"
          className={`transition-transform ${aberto ? "rotate-180" : ""}`}
        />
      </button>

      {aberto && (
        <div className="grid gap-3 border-t border-border px-4 pb-4 pt-3 sm:grid-cols-2">
          <CampoSelect
            label="Situação"
            name="filtroSituacao"
            value={filtros.situacao}
            onChange={(e) => onMudar({ situacao: e.target.value as FiltroSituacao })}
          >
            <option value="todos">Todas</option>
            <option value="nao_contatado">Não contatado</option>
            <option value="contatado">Contatado</option>
            <option value="precisa">Precisa de acompanhamento</option>
          </CampoSelect>

          <CampoSelect
            label="Data da visita"
            name="filtroPeriodo"
            value={filtros.periodo}
            onChange={(e) => onMudar({ periodo: e.target.value as FiltroPeriodo })}
          >
            <option value="todos">Qualquer data</option>
            <option value="hoje">Hoje</option>
            <option value="7">Últimos 7 dias</option>
            <option value="30">Últimos 30 dias</option>
            <option value="90">Últimos 90 dias</option>
            <option value="custom">Período personalizado</option>
          </CampoSelect>

          {filtros.periodo === "custom" && (
            <div className="sm:col-span-2">
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="De"
                  name="filtroDe"
                  type="date"
                  max={hoje}
                  value={filtros.de}
                  onChange={(e) => onMudar({ de: e.target.value })}
                />
                <Input
                  label="Até"
                  name="filtroAte"
                  type="date"
                  max={hoje}
                  value={filtros.ate}
                  onChange={(e) => onMudar({ ate: e.target.value })}
                />
              </div>
              {msgPeriodo && (
                <p role="alert" className="mt-1.5 text-xs text-danger">
                  {msgPeriodo}
                </p>
              )}
            </div>
          )}

          <CampoSelect
            label="Faixa etária"
            name="filtroFaixa"
            value={filtros.faixa}
            onChange={(e) => onMudar({ faixa: e.target.value as FiltroFaixa })}
          >
            <option value="todas">Todas</option>
            {FAIXAS.map((f) => (
              <option key={f.chave} value={f.chave}>
                {f.plural} ({f.max === null ? `${f.min}+` : `${f.min}–${f.max}`})
              </option>
            ))}
            <option value="custom">Faixa personalizada</option>
          </CampoSelect>

          <CampoSelect
            label="Ordenar por"
            name="filtroOrdem"
            value={filtros.ordem}
            onChange={(e) => onMudar({ ordem: e.target.value as Ordenacao })}
          >
            <option value="recente">Mais recentes</option>
            <option value="nome">Nome (A–Z)</option>
            <option value="visitas">Mais visitas</option>
          </CampoSelect>

          {filtros.faixa === "custom" && (
            <div className="sm:col-span-2">
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Idade mínima"
                  name="filtroIdadeMin"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={120}
                  value={filtros.idadeMin}
                  onChange={(e) =>
                    onMudar({ idadeMin: e.target.value.replace(/\D/g, "").slice(0, 3) })
                  }
                />
                <Input
                  label="Idade máxima"
                  name="filtroIdadeMax"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={120}
                  value={filtros.idadeMax}
                  onChange={(e) =>
                    onMudar({ idadeMax: e.target.value.replace(/\D/g, "").slice(0, 3) })
                  }
                />
              </div>
              {msgIdade && (
                <p role="alert" className="mt-1.5 text-xs text-danger">
                  {msgIdade}
                </p>
              )}
            </div>
          )}

          <div className="sm:col-span-2">
            <button
              type="button"
              onClick={onLimpar}
              className="min-h-9 rounded-lg border border-border px-3 text-[13px] font-semibold text-text transition-colors hover:bg-surface-2"
            >
              Limpar filtros
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
